import { useState } from 'react'
import { useModals } from 'almanac-ui/Modal'
import { AnalysingModal } from '../SourceMaterialModal/AnalysingModal'
import { SourcePreviewModal } from '../SourceMaterialModal/SourcePreviewModal'
import { useIsUrlCrawlable } from 'hooks/useIsUrlCrawlable'
import { useCreateSourceSync } from 'hooks/queries/workspaces/useCreateSourceSync'
import { useCreateMediaObjects } from 'hooks/queries/mediaObjects/useCreateMediaObjects'
import { CONTENT_CHAR_LIMIT, type ScreenType } from './constants'
import type {
  KnowledgeBaseSourceMaterial,
  PlainText,
  Website,
  AlmanacFileSource,
  SourceMaterialsMediaObject,
  Source,
} from 'types'

type SourceMaterialWithUrl = KnowledgeBaseSourceMaterial & { source_url: string }

const isWebsite = (source: Source): source is Website => source.type === 'Website'
const isPlainText = (source: Source): source is PlainText => source.type === 'PlainText'
const isSourceMaterialsMediaObject = (source: Source): source is SourceMaterialsMediaObject =>
  source.type === 'SourceMaterialsMediaObject'

interface SubmissionInputs {
  url: string
  text: string
  file: File | null
  fileContent: string
  scrapeImages?: boolean
}

interface UseSourceMaterialModalParams {
  currentScreen: ScreenType | null
  onHandleSourceMaterialSuccess?: (sourceMaterial: KnowledgeBaseSourceMaterial) => void
  onSuccess?: (source: PlainText | Website | AlmanacFileSource | SourceMaterialsMediaObject) => void
  onError?: () => void
}

export const useSourceMaterialModal = ({
  currentScreen,
  onHandleSourceMaterialSuccess,
  onSuccess,
  onError,
}: UseSourceMaterialModalParams) => {
  const { openModal } = useModals()
  const { mutateAsync: createSourceSync } = useCreateSourceSync()
  const { mutateAsync: createMediaObjects } = useCreateMediaObjects()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [url, setUrl] = useState('')
  const [text, setText] = useState('')
  const [fileContent, setFileContent] = useState('')

  const { isValid: isValidUrl } = useIsUrlCrawlable(url)

  const isValid =
    (currentScreen === 'website' && url.length > 0 && isValidUrl) ||
    (currentScreen === 'text' && text.length > 0) ||
    (currentScreen === 'file' && fileContent.length > 0)

  const submitWithCallback = async (screen: ScreenType, inputs: SubmissionInputs) => {
    if (screen === 'website') {
      const data = await createSourceSync({
        type: 'Website',
        url: inputs.url,
        scrape_images: inputs.scrapeImages,
      })
      if (isWebsite(data.source)) {
        onSuccess?.(data.source)
      }
    } else if (screen === 'text') {
      const data = await createSourceSync({ type: 'PlainText', text: inputs.text })
      if (isPlainText(data.source)) {
        onSuccess?.(data.source)
      }
    } else if (screen === 'file' && inputs.file) {
      const mediaObjects = await createMediaObjects([{ file: inputs.file, filename: inputs.file.name }])
      // Our file picker only allows one file at a time
      const data = await createSourceSync({ type: 'SourceMaterialsMediaObject', media_object_id: mediaObjects[0].id })
      if (isSourceMaterialsMediaObject(data.source)) {
        onSuccess?.(data.source)
      }
    }
  }

  const submitWithModal = (screen: ScreenType, inputs: SubmissionInputs) => {
    if (!onHandleSourceMaterialSuccess) return

    if (screen === 'website') {
      const sourceMaterial: SourceMaterialWithUrl = {
        source_url: inputs.url,
      } as SourceMaterialWithUrl

      openModal(AnalysingModal, {
        onError,
        onHandleSourceMaterialSuccess,
        sourceMaterial,
      })
    } else if (screen === 'text') {
      openModal(SourcePreviewModal, {
        sourceMaterial: {
          name: '',
          emoji: null,
          color: null,
          content: inputs.text.substring(0, CONTENT_CHAR_LIMIT),
        },
        onHandleSourceMaterialSuccess,
      })
    } else if (screen === 'file') {
      openModal(SourcePreviewModal, {
        sourceMaterial: {
          name: '',
          emoji: null,
          color: null,
          content: inputs.fileContent.substring(0, CONTENT_CHAR_LIMIT),
          ...(inputs.file && { source_file: inputs.file }),
        },
        onHandleSourceMaterialSuccess,
      })
    }
  }

  const submit = async (screen: ScreenType | null, inputs: SubmissionInputs) => {
    if (!screen) return

    setIsSubmitting(true)
    try {
      if (onSuccess) {
        await submitWithCallback(screen, inputs)
      } else if (onHandleSourceMaterialSuccess) {
        submitWithModal(screen, inputs)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    url,
    setUrl,
    text,
    setText,
    fileContent,
    setFileContent,
    isValid,
    isSubmitting,
    submit,
  }
}
